import { useCallback, useEffect, useMemo, useState } from 'react';
import { TripsLayer } from '@deck.gl/geo-layers';
import type { GisLine } from '@/utils/gisUtils';

export type { GisLine } from '@/utils/gisUtils';

export interface FlowLink extends GisLine {
	timestamps: number[];
	flowId?: string;
}

interface FlowData {
	smFlows: FlowLink[];
	mdFlows: FlowLink[];
	bgFlows: FlowLink[];
}

const FLOW_PRIMARY: [number, number, number, number] = [28, 41, 147, 255];

const multiply = 100;
const maxTime = 10;
const step = 2;
const TRAIL_LENGTH = 150;

export function useFlowAnimation(flowLines: GisLine[]) {
	const [animationTime, setAnimationTime] = useState<number>(0);
	const [runAnimation, setRunAnimation] = useState<boolean>(false);

	useEffect(() => {
		if (!runAnimation) return;
		let frameId: number;
		const loop = () => {
			setAnimationTime(prev => (prev + step) % (maxTime * multiply * 2));
			frameId = requestAnimationFrame(loop);
		};
		frameId = requestAnimationFrame(loop);
		return () => cancelAnimationFrame(frameId);
	}, [runAnimation]);

	const flowData = useMemo<FlowData | null>(() => {
		if (!flowLines.length) return null;
		const cycleTime = maxTime * multiply;
		const withTimestamps: FlowLink[] = flowLines
			.filter((l) => l.path.length >= 2)
			.map((line) => ({
				...line,
				timestamps: Array.from(
					{ length: line.path.length },
					(_, i) => (i * cycleTime) / (line.path.length - 1 || 1),
				),
			}));
		const sm = withTimestamps.filter((l) => l.sizePerc > 0 && l.sizePerc <= 0.3);
		const md = withTimestamps.filter((l) => l.sizePerc > 0.3 && l.sizePerc <= 0.7);
		const bg = withTimestamps.filter((l) => l.sizePerc > 0.7 || l.sizePerc === 0);

		const createOffsetData = (data: FlowLink[], numFlows = 2): FlowLink[] => {
			const result: FlowLink[] = [];
			data.forEach((line, lineIndex) => {
				for (let i = 0; i < numFlows; i++) {
					const offset = (i * cycleTime) / numFlows;
					result.push({
						...line,
						flowId: `${lineIndex}_${i}`,
						timestamps: line.timestamps.map((t) => t + offset),
					});
				}
			});
			return result;
		};

		return {
			smFlows: createOffsetData(sm, 2),
			mdFlows: createOffsetData(md, 2),
			bgFlows: createOffsetData(bg, 2),
		};
	}, [flowLines]);

	const tripLayers = useMemo(() => {
		if (!flowData || !runAnimation) return [];
		const { smFlows, mdFlows, bgFlows } = flowData;
		return [
			new TripsLayer<FlowLink>({
				id: 'tripsS',
				data: smFlows,
				getPath: (d) => d.path,
				getTimestamps: (d) => d.timestamps,
				getColor: () => FLOW_PRIMARY,
				fadeTrail: true,
				opacity: 0.9,
				widthMinPixels: 2,
				rounded: true,
				trailLength: TRAIL_LENGTH,
				currentTime: animationTime,
			}),
			new TripsLayer<FlowLink>({
				id: 'tripsM',
				data: mdFlows,
				getPath: (d) => d.path,
				getTimestamps: (d) => d.timestamps,
				getColor: () => FLOW_PRIMARY,
				fadeTrail: true,
				opacity: 0.9,
				widthMinPixels: 3,
				rounded: true,
				trailLength: TRAIL_LENGTH,
				currentTime: animationTime,
			}),
			new TripsLayer<FlowLink>({
				id: 'tripsB',
				data: bgFlows,
				getPath: (d) => d.path,
				getTimestamps: (d) => d.timestamps,
				getColor: () => FLOW_PRIMARY,
				fadeTrail: true,
				opacity: 0.9,
				widthMinPixels: 4,
				rounded: true,
				trailLength: TRAIL_LENGTH,
				currentTime: animationTime,
			}),
		];
	}, [flowData, runAnimation, animationTime]);

	const changeAnimate = useCallback((val: boolean) => {
		if (val) {
			setAnimationTime(0);
			setRunAnimation(true);
		} else {
			setRunAnimation(false);
		}
	}, []);

	return { tripLayers, changeAnimate };
}
